import React, { useState } from "react";
import { CancelToken } from "axios";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import {
  FileCopyOutlined as FileIcon,
  EmailOutlined as EmailIcon,
  PrintOutlined as PrinterIcon,
  CloseOutlined as CancelIcon,
} from "@material-ui/icons";
import {
  Grid,
  ListItemIcon,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { useStyles } from "./detailStyles";
import useDetail from "./useDetail";
import EmailDialog from "./EmailDialog";
import translate from "../../../i18n/translate";

const DetailActions = ({
  id,
  url,
  showCancelButton,
  updateTransactions,
  onClose,
}) => {
  const classes = useStyles();
  const [emailError, setEmailError] = useState();
  const [showDialog, setShowDialog] = useState(false);
  const { refundLoading, sendLoading, refund, send } = useDetail(id);

  const intl = useIntl();

  const onGet = () => {
    window.open(url, "_blank");
  };

  const onPrint = () => {
    window.open(`${url}?print`, "_blank");
  };

  const onRefund = async () => {
    if (refundLoading) {
      return;
    }

    const resp = await refund();

    if (resp?.data?.success) {
      toast.success(
        translate("resume.transactionDetails.toastMessages.refundSuccess")
      );
    } else {
      toast.error(
        resp?.data?.error?.description ||
          translate("resume.toastMessages.error")
      );
    }

    const source = CancelToken.source();
    updateTransactions(source);
    onClose();
  };

  const onSend = async ({ email }) => {
    setEmailError();

    const resp = await send(email);

    if (resp?.data?.success) {
      setShowDialog(false);
      toast.success(
        translate("resume.transactionDetails.toastMessages.sendSuccess")
      );
    } else {
      setEmailError(
        resp?.data?.error?.description ||
          translate("resume.toastMessages.error")
      );
    }
  };

  return (
    <>
      <EmailDialog
        onClose={() => {
          setShowDialog(false);
          setEmailError();
        }}
        onSubmit={onSend}
        isSubmitting={sendLoading}
        show={showDialog}
        error={emailError}
      />
      <Grid
        component="section"
        aria-label={intl.formatMessage({
          id: "transactions.details.actions",
        })}
        container
        justifyContent="space-between"
        className={classes.actionPanelContainer}
      >
        <Grid item className={classes.buttonsList}>
          <List dense>
            <ListItem button onClick={onGet}>
              <ListItemIcon>
                <FileIcon />
              </ListItemIcon>
              <ListItemText>
                {translate("resume.transactionDetails.buttons.show")}
              </ListItemText>
            </ListItem>
            <ListItem
              button
              className={classes.buttonMargin}
              onClick={() => setShowDialog(true)}
            >
              <ListItemIcon>
                <EmailIcon />
              </ListItemIcon>
              <ListItemText>
                {translate("resume.transactionDetails.buttons.shareViaEmail")}
              </ListItemText>
            </ListItem>
            <ListItem button className={classes.buttonMargin} onClick={onPrint}>
              <ListItemIcon>
                <PrinterIcon />
              </ListItemIcon>
              <ListItemText>
                {translate("resume.transactionDetails.buttons.print")}
              </ListItemText>
            </ListItem>
          </List>
        </Grid>
        {showCancelButton && (
          <Grid item className={classes.returnButtonList}>
            <List dense>
              <ListItem button disabled={refundLoading} onClick={onRefund}>
                <ListItemIcon>
                  <CancelIcon />
                </ListItemIcon>
                <ListItemText>
                  {translate(
                    "resume.transactionDetails.buttons.returnTransaction"
                  )}
                </ListItemText>
              </ListItem>
            </List>
          </Grid>
        )}
      </Grid>
    </>
  );
};

DetailActions.propTypes = {
  id: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  showCancelButton: PropTypes.bool.isRequired,
};

export default DetailActions;
